import {
  Dispatch,
  FormEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { CircularProgressbar } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router';
import Webcam from 'react-webcam';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import check from '../../../assets/icons/checkCircleWhite.svg';
import editPen from '../../../assets/icons/squareEditPen.svg';
import icon from '../../../assets/images/backoffice/kyc4.png';
import { AnimatedDashboard, ErrorToast } from '../../../components';
import CameraButton from '../../../components/common/CameraButton';
import ModalComponentCamera from '../../../components/common/ModalComponentCamera';
import Spinner from '../../../components/common/Spinner';
import {
  convertImage,
  videoConstraints
} from '../../../helpers/helperFunctions';
import { H2, H3, P1, P2 } from '../../../styles/FontStyles';
import { Button } from '../../../styles/GlobalComponents';
import { colors } from '../../../styles/GlobalStyles';
import { Flex, FlexCenter } from '../../../styles/stylesStyled';
import {
  CamCaptured,
  Camera,
  CameraUnderlineText,
  Desktop,
  ImageEditOverlay,
  ImageUploadedOverlay,
  ImageUploadingOverlay,
  KycBrowseFilesButton,
  Mobile,
  TitleImage
} from '../KycStyled';
import { addKycDocumentImage } from './KycDocumentPhotos.actions';
import { RenderDriversLicense } from './RenderDriversLicense';
import { RenderID } from './RenderID';
import { RenderPassport } from './RenderPassport';

interface ImgStateType {
  state: number;
  setImgState: Dispatch<SetStateAction<number>>;
  percentage: number;
  uploadHandler: any;
  retake: any;
}

export const ImgState = ({
  state,
  setImgState,
  percentage,
  uploadHandler,
  retake
}: ImgStateType) => {
  const { t } = useTranslation('kyc');
  switch (state) {
    case 1:
      return (
        <ImageUploadedOverlay>
          <Flex>
            <img
              src={check}
              alt='Uploaded'
              style={{ marginRight: 8 }}
              // onClick={() => setImgState(2)}
            />
            {t('document_photos.uploaded_successfully')}
          </Flex>
          <img src={editPen} alt='Uploaded' onClick={() => setImgState(3)} />
        </ImageUploadedOverlay>
      );
    case 3:
      return (
        <ImageEditOverlay>
          <FlexCenter style={{ justifyContent: 'center' }}>
            <Button
              type='button'
              fs='12px'
              h='34px'
              maxWidth='111px'
              mr='12'
              onClick={() => retake()}>
              {t('document_photos.retake_photo')}
            </Button>
            <KycBrowseFilesButton>
              <input
                type='file'
                accept='image/*'
                style={{ display: 'none' }}
                onChange={(e) => uploadHandler(e)}
              />
              {t('document_photos.reupload_photo')}
            </KycBrowseFilesButton>
          </FlexCenter>
        </ImageEditOverlay>
      );
    case 2:
      return (
        <ImageUploadingOverlay>
          <div>
            <span />

            <div>{t('document_photos.uploading')}</div>
          </div>
          <div style={{ width: 38 }}>
            <CircularProgressbar
              value={percentage}
              text={`${percentage}%`}
              maxValue={100}
              styles={{
                // Customize the root svg element
                root: {},
                // Customize the path, i.e. the "completed progress"
                path: {
                  // Path color
                  stroke: `rgba(255, 255, 255)`,
                  // Whether to use rounded or flat corners on the ends - can use 'butt' or 'round'
                  strokeLinecap: 'round',
                  // Customize transition animation
                  transition: 'stroke-dashoffset 0.5s ease 0s',
                  // Rotate the path
                  // transform: 'rotate(0.25turn)',
                  transformOrigin: 'center center'
                },
                // Customize the circle behind the path, i.e. the "total progress"
                trail: {
                  // Trail color
                  stroke: 'none',
                  // Whether to use rounded or flat corners on the ends - can use 'butt' or 'round'
                  strokeLinecap: 'butt',
                  // Rotate the trail
                  transform: 'rotate(0.25turn)',
                  transformOrigin: 'center center'
                },
                // Customize the text
                text: {
                  // Text color
                  fill: '#fff',
                  // Text size
                  fontSize: '30px',
                  fontWeight: 500
                },
                // Customize background - only used when the `background` prop is true
                background: {
                  fill: 'none'
                }
              }}
              strokeWidth={4}
            />
          </div>
        </ImageUploadingOverlay>
      );
    default:
      return null;
  }
};

type TParams = { id: string };

const KycDocumentPhotos = ({
  history,
  match
}: RouteComponentProps<TParams>) => {
  const { t } = useTranslation('kyc');

  const [id] = useState(parseInt(match.params.id));
  const [imageFrontUrl, setImageFrontUrl] = useState('');
  const [imageBackUrl, setImageBackUrl] = useState('');
  const [passport, setPassport] = useState('');
  const [driversLicense, setDriversLicense] = useState('');

  const [documentFrontId, setDocumentFrontId] = useState<string | File>('');
  const [documentBackId, setDocumentBackId] = useState<string | File>('');
  const [documentPassport, setDocumentPassport] = useState<string | File>('');
  const [documentDriversLicense, setDocumentDriversLicense] = useState<
    string | File
  >('');

  const [imgStateFront, setImgStateFront] = useState(1);
  const [imgStateBack, setImgStateBack] = useState(1);
  const [imgStatePassport, setImgStatePassport] = useState(1);
  const [imgStateDriversLicense, setImgStateDriversLicense] = useState(1);

  const [image, setImage] = useState('');
  const [image2, setImage2] = useState('');
  const [image3, setImage3] = useState('');
  const [image4, setImage4] = useState('');

  const [errorMsg, setErrorMsg] = useState('');

  const [modal, setModal] = useState(false);
  const [modal2, setModal2] = useState(false);
  const [modal3, setModal3] = useState(false);
  const [modal4, setModal4] = useState(false);

  const webcamRef = useRef<any>(null);

  const dispatch = useAppDispatch();

  const { loading } = useAppSelector((state) => state.kyc);

  const uploadIdFrontHandler = async (e: any) => {
    const fileUploaded = e.target.files[0];
    setImageFrontUrl(URL.createObjectURL(fileUploaded));
    setDocumentFrontId(fileUploaded);
    setImgStateFront(1);
  };

  const captureIdFront = useCallback(async () => {
    setImageFrontUrl('');
    const imageSrc = webcamRef?.current?.getScreenshot();
    setImage(imageSrc);
    const frontImage = await convertImage(imageSrc, 'frontId.jpeg');
    setDocumentFrontId(frontImage);
    setImageFrontUrl(imageSrc);
  }, [webcamRef, setImage]);

  const uploadIdBackHandler = (e: any) => {
    const fileUploaded = e.target.files[0];
    setImageBackUrl(URL.createObjectURL(fileUploaded));
    setDocumentBackId(fileUploaded);
    setImgStateBack(1);
  };

  const captureIdBack = useCallback(async () => {
    setImageBackUrl('');
    const imageSrc = webcamRef?.current?.getScreenshot();

    setImage2(imageSrc);

    const backImage = await convertImage(imageSrc, 'backId.jpeg');

    setDocumentBackId(backImage);

    setImageBackUrl(imageSrc);
  }, [webcamRef, setImage2]);

  const uploadPassportHandler = (e: any) => {
    const fileUploaded = e.target.files[0];

    setPassport(URL.createObjectURL(fileUploaded));

    setDocumentPassport(fileUploaded);
  };

  const capturePassport = useCallback(async () => {
    setPassport('document_photos.');
    const imageSrc = webcamRef?.current?.getScreenshot();

    setImage3(imageSrc);

    const passportImage = await convertImage(imageSrc, 'passport.jpeg');

    setDocumentPassport(passportImage);

    setPassport(imageSrc);
  }, [webcamRef, setImage3]);

  const uploadLicenseHandler = (e: any) => {
    const fileUploaded = e.target.files[0];

    setDriversLicense(URL.createObjectURL(fileUploaded));

    setDocumentDriversLicense(fileUploaded);
  };

  const captureDriversLicense = useCallback(async () => {
    setDriversLicense('');
    const imageSrc = webcamRef?.current?.getScreenshot();

    setImage4(imageSrc);

    const driversLicenseImage = await convertImage(
      imageSrc,
      'driversLicense.jpeg'
    );

    setDocumentDriversLicense(driversLicenseImage);

    setDriversLicense(imageSrc);
  }, [webcamRef, setImage4]);

  const percentage = 66;

  const onHandleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log('SUBMIT');

    if (id === 1) {
      if (!imageFrontUrl) {
        ErrorToast(t('document_photos.err_front_ID_required'));
      } else if (!imageBackUrl) {
        ErrorToast(t('document_photos.err_back_ID_required'));
      } else {
        uploadIdImages();
      }
    } else if (id === 2) {
      if (!driversLicense) {
        ErrorToast(t('document_photos.err_driver_license_required'));
      } else {
        uploadDiverLicense();
      }
    } else {
      if (!passport) {
        ErrorToast(t('document_photos.err_password_required'));
      } else {
        uploadPassport();
      }
    }
  };

  const uploadIdImages = () => {
    const formData = new FormData();
    formData.append('images', documentFrontId);
    formData.append('images', documentBackId);

    dispatch(addKycDocumentImage({ formData, history }));
  };

  const uploadPassport = () => {
    const formData = new FormData();
    formData.append('images', documentPassport);

    dispatch(addKycDocumentImage({ formData, history }));
  };

  const uploadDiverLicense = () => {
    const formData = new FormData();
    formData.append('images', documentDriversLicense);

    dispatch(addKycDocumentImage({ formData, history }));
  };

  useEffect(() => {
    setImgStateFront(1);
    setImgStateBack(1);
    setImgStatePassport(1);
  }, [modal, modal2, modal3]);

  return (
    <AnimatedDashboard key={'kyc-document-photos'}>
      {loading && <Spinner fullScreen={true} transparent />}

      <>
        <Desktop>
          <TitleImage>
            <img
              src={icon}
              alt='icon'
              style={{ marginBottom: 40, width: 100 }}
            />
          </TitleImage>
        </Desktop>
        <Desktop>
          <H2 mb='12' color={colors.neutral900}>
            {t('document_photos.title')}
          </H2>

          <P1
            mb='40'
            maxWidth='600px'
            maxWidthSM='1000px'
            color={colors.neutral500}>
            {t('document_photos.subtitle', {
              and_back:
                id === 1 ? t('document_photos.subtitle_and_back_side') : ''
            })}
          </P1>
        </Desktop>
        <Mobile>
          <H3 mb='8' center color={colors.neutral900}>
            {t('document_photos.title')}
          </H3>

          <P2 mb='32' center color={colors.neutral500}>
            {t('document_photos.subtitle', {
              and_back:
                id === 1 ? t('document_photos.subtitle_and_back_side') : ''
            })}
          </P2>
        </Mobile>

        <form onSubmit={onHandleSubmit}>
          <RenderID
            id={id}
            imgStateFront={imgStateFront}
            setImgStateFront={setImgStateFront}
            imgStateBack={imgStateBack}
            setImgStateBack={setImgStateBack}
            imageFrontUrl={imageFrontUrl}
            imageBackUrl={imageBackUrl}
            uploadIdFrontHandler={uploadIdFrontHandler}
            uploadIdBackHandler={uploadIdBackHandler}
            setImage={setImage}
            setModal={setModal}
            setImage2={setImage2}
            setModal2={setModal2}
            percentage={percentage}
            setImageFrontUrl={setImageFrontUrl}
            setDocumentFrontId={setDocumentFrontId}
            setImageBackUrl={setImageBackUrl}
            setDocumentBackId={setDocumentBackId}
          />

          <RenderDriversLicense
            id={id}
            driversLicense={driversLicense}
            imgStateDriversLicense={imgStateDriversLicense}
            setImgStateDriversLicense={setImgStateDriversLicense}
            uploadLicenseHandler={uploadLicenseHandler}
            setImage4={setImage4}
            setModal4={setModal4}
            percentage={percentage}
            setDocumentDriversLicense={setDocumentDriversLicense}
            setDriversLicense={setDriversLicense}
          />

          <RenderPassport
            id={id}
            percentage={percentage}
            setModal3={setModal3}
            setImage3={setImage3}
            uploadPassportHandler={uploadPassportHandler}
            setImgStatePassport={setImgStatePassport}
            imgStatePassport={imgStatePassport}
            passport={passport}
            setPassport={setPassport}
            setDocumentPassport={setDocumentPassport}
          />

          <Desktop>
            <Button maxWidth='200px'>
              {t('document_photos.confirm_photos_button')}
            </Button>
          </Desktop>

          <Mobile>
            <Button mbSM='50'>
              {t('document_photos.confirm_photos_button')}
            </Button>
          </Mobile>
        </form>

        {/* TAKE A PICTURE MODALS --------------------------------------------------------------------- */}

        <ModalComponentCamera modal={modal} setModal={setModal}>
          {image ? (
            <div>
              <P2 bold center mb='30'>
                {t('document_photos.camera_modal_title')}
              </P2>
              <CamCaptured>
                <img src={image} alt='captured' />
              </CamCaptured>
              <Button
                opacity={true}
                onClick={() => {
                  setModal(!modal);
                  setImgStateFront(1);
                }}
                type='button'
                m='20px 0'>
                {t('document_photos.continue')}
              </Button>
              <CameraUnderlineText onClick={() => setImage('')}>
                {t('document_photos.retake')}
              </CameraUnderlineText>
            </div>
          ) : (
            <div>
              <P2 bold center mb='30'>
                {t('document_photos.camera_modal_title2')}
              </P2>
              <Camera>
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  videoConstraints={videoConstraints}
                  screenshotFormat='image/jpeg'
                  screenshotQuality={1}
                  minScreenshotWidth={2000}
                />
              </Camera>
              <CameraButton click={captureIdFront} />
              <CameraUnderlineText onClick={() => setModal(false)}>
                {t('document_photos.cancel')}
              </CameraUnderlineText>
            </div>
          )}
        </ModalComponentCamera>
        <ModalComponentCamera modal={modal2} setModal={setModal2}>
          {image2 ? (
            <div>
              <P2 bold center mb='30'>
                {t('document_photos.camera_modal_title')}
              </P2>
              <CamCaptured>
                <img src={image2} alt='captured' />
              </CamCaptured>
              <Button
                opacity={true}
                onClick={() => {
                  setModal2(!modal2);
                  setImgStateBack(1);
                }}
                type='button'
                m='20px 0'>
                {t('document_photos.continue')}
              </Button>
              <CameraUnderlineText onClick={() => setImage2('')}>
                {t('document_photos.retake')}
              </CameraUnderlineText>
            </div>
          ) : (
            <div>
              <P2 bold center mb='30'>
                {t('document_photos.camera_modal_title3')}
              </P2>
              <Camera>
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  videoConstraints={videoConstraints}
                  screenshotFormat='image/jpeg'
                  screenshotQuality={1}
                  minScreenshotWidth={2000}
                />
              </Camera>
              <CameraButton click={captureIdBack} />
              <CameraUnderlineText onClick={() => setModal2(false)}>
                {t('document_photos.cancel')}
              </CameraUnderlineText>
            </div>
          )}
        </ModalComponentCamera>
        <ModalComponentCamera modal={modal3} setModal={setModal3}>
          <P2 bold center mb='30'>
            {t('document_photos.passport_camera_modal_title')}
          </P2>
          {image3 ? (
            <div>
              <CamCaptured>
                <img src={image3} alt='captured' />
              </CamCaptured>
              <Button
                opacity={'true'}
                type='button'
                onClick={() => {
                  setModal3(!modal3);
                  setImgStatePassport(1);
                }}
                m='20px 0'>
                {t('document_photos.continue')}
              </Button>
              <CameraUnderlineText onClick={() => setImage3('')}>
                {t('document_photos.retake')}
              </CameraUnderlineText>
            </div>
          ) : (
            <div>
              <Camera>
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat='image/jpeg'
                  screenshotQuality={1}
                  width='100%'
                  videoConstraints={videoConstraints}
                />
              </Camera>
              <CameraButton click={capturePassport} />
              <CameraUnderlineText onClick={() => setModal3(false)}>
                {t('document_photos.cancel')}
              </CameraUnderlineText>
            </div>
          )}
        </ModalComponentCamera>
        <ModalComponentCamera modal={modal4} setModal={setModal4}>
          <P2 bold center mb='30'>
            {t('document_photos.driver_license_camera_modal_title')}
          </P2>
          {image4 ? (
            <div>
              <CamCaptured>
                <img src={image4} alt='captured' />
              </CamCaptured>
              <Button
                opacity={'true'}
                type='button'
                onClick={() => {
                  setModal4(!modal4);
                  setImgStateDriversLicense(1);
                }}
                m='20px 0'>
                {t('document_photos.continue')}
              </Button>
              <CameraUnderlineText onClick={() => setImage4('')}>
                {t('document_photos.retake')}
              </CameraUnderlineText>
            </div>
          ) : (
            <div>
              <Camera>
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  videoConstraints={videoConstraints}
                  screenshotFormat='image/jpeg'
                  screenshotQuality={1}
                  width='100%'
                />
              </Camera>
              <CameraButton click={captureDriversLicense} />
              <CameraUnderlineText onClick={() => setModal4(false)}>
                {t('document_photos.cancel')}
              </CameraUnderlineText>
            </div>
          )}
        </ModalComponentCamera>
        {/* ----------------------------------------------------------------------- TAKE A PICTURE MODALS*/}
      </>
    </AnimatedDashboard>
  );
};

export default KycDocumentPhotos;
